import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Button, Card, Field, Input, useTheme, useToast } from "@aigntx/ui";
import type { FontFamily, Locale, ThemeMode } from "@aigntx/ui";
import { endpoints } from "../api";
import { useAuth } from "../auth";
import { useNotify } from "../notify";
import type { UserSettings } from "../types";

const MODES: ThemeMode[] = ["light", "dark"];
const LOCALES: Locale[] = ["en", "ar"];
const FONTS: FontFamily[] = ["sans", "grotesk"];

export function SettingsPage() {
  const { theme, setMode, setLocale, setTheme } = useTheme();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [saved, setSaved] = useState(false);

  function persist(patch: Partial<UserSettings>) {
    const done = () => {
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1400);
    };
    if (user) void endpoints.saveSettings(patch).then(done).catch(() => {});
    else done();
  }

  function chooseMode(m: ThemeMode) {
    setMode(m);
    persist({ mode: m });
  }
  function chooseLocale(l: Locale) {
    setLocale(l);
    void i18n.changeLanguage(l);
    persist({ locale: l });
  }
  function chooseFont(f: FontFamily) {
    setTheme({ font: f });
    persist({ font: f });
  }

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t("settings.title")}</h1>
        <p className="page__sub">{t("settings.subtitle")}</p>
      </header>

      {user && <ProfileCard />}

      <Card className="settings">
        <div className="settings__row">
          <span className="settings__label">{t("settings.mode")}</span>
          <div className="segmented">
            {MODES.map((m) => (
              <button
                key={m}
                className={theme.mode === m ? "seg is-on" : "seg"}
                onClick={() => chooseMode(m)}
              >
                {t(`settings.${m}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="settings__row">
          <span className="settings__label">{t("settings.language")}</span>
          <div className="segmented">
            {LOCALES.map((l) => (
              <button
                key={l}
                className={theme.locale === l ? "seg is-on" : "seg"}
                onClick={() => chooseLocale(l)}
              >
                {t(`lang.${l}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="settings__row">
          <span className="settings__label">{t("settings.font")}</span>
          <div className="segmented">
            {FONTS.map((f) => (
              <button
                key={f}
                className={theme.font === f ? "seg is-on" : "seg"}
                onClick={() => chooseFont(f)}
              >
                {t(`settings.${f}`)}
              </button>
            ))}
          </div>
        </div>

        <div className={saved ? "settings__saved is-on" : "settings__saved"} aria-live="polite">
          {saved ? t("settings.saved") : ""}
        </div>
      </Card>

      {user && <NotificationsCard />}
      {user && <PasswordCard />}
    </div>
  );
}

function NotificationsCard() {
  const { t } = useTranslation();
  const { notify, setNotify } = useNotify();
  const supported = typeof Notification !== "undefined";
  const [perm, setPerm] = useState<string>(() => (supported ? Notification.permission : "unsupported"));

  async function enable() {
    if (!supported) return;
    try {
      setPerm(await Notification.requestPermission());
    } catch {
      /* ignore */
    }
  }

  return (
    <Card title={t("notif.permTitle")} subtitle={t("notif.permHint")} className="settings">
      {/* Master switch — works even once the browser permission is granted, so
          notifications can always be turned back off from inside the app. */}
      <div className="settings__row">
        <span className="settings__label">{t("notif.masterLabel")}</span>
        <div className="segmented">
          <button className={notify ? "seg is-on" : "seg"} onClick={() => setNotify(true)}>
            {t("notif.on")}
          </button>
          <button className={!notify ? "seg is-on" : "seg"} onClick={() => setNotify(false)}>
            {t("notif.off")}
          </button>
        </div>
      </div>

      {!notify ? (
        <p className="muted">{t("notif.mutedHint")}</p>
      ) : !supported ? (
        <p className="muted">{t("notif.unsupported")}</p>
      ) : perm === "granted" ? (
        <div className="form-ok">{t("notif.permEnabled")}</div>
      ) : perm === "denied" ? (
        <p className="muted">{t("notif.permDenied")}</p>
      ) : (
        <div className="brand-actions">
          <Button onClick={() => void enable()}>{t("notif.permEnable")}</Button>
        </div>
      )}
    </Card>
  );
}

function ProfileCard() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const toast = useToast();
  const [name, setName] = useState(user?.name ?? "");
  const [avatar, setAvatar] = useState(user?.avatarUrl ?? "");
  const [busy, setBusy] = useState(false);

  function pickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_500_000) {
      toast.error(t("profile.tooBig"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAvatar(String(reader.result));
    reader.readAsDataURL(file);
  }

  async function save() {
    setBusy(true);
    try {
      setUser(await endpoints.updateProfile({ name, avatarUrl: avatar }));
      toast.success(t("profile.saved"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t("profile.title")} subtitle={t("profile.subtitle")} className="settings">
      <div className="profile-avatar">
        <Avatar src={avatar || undefined} name={name} size={72} />
        <div className="profile-avatar__actions">
          <label className="ax-btn ax-btn--outline ax-btn--sm">
            {t("profile.choose")}
            <input type="file" accept="image/*" onChange={pickFile} hidden />
          </label>
          {avatar && (
            <Button size="sm" variant="ghost" onClick={() => setAvatar("")}>
              {t("profile.remove")}
            </Button>
          )}
        </div>
      </div>
      <Field label={t("users.name")}>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="brand-actions">
        <Button loading={busy} onClick={() => void save()}>
          {t("common.save")}
        </Button>
      </div>
    </Card>
  );
}

function PasswordCard() {
  const { t } = useTranslation();
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (next !== confirm) {
      setMsg({ ok: false, text: t("account.mismatch") });
      return;
    }
    setBusy(true);
    try {
      await endpoints.changePassword(current, next);
      setMsg({ ok: true, text: t("account.changed") });
      toast.success(t("account.changed"));
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      const text = err instanceof Error ? err.message : String(err);
      setMsg({ ok: false, text });
      toast.error(text);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t("account.password")} subtitle={t("account.passwordHint")} className="settings">
      <form className="pw-form" onSubmit={submit}>
        <Field label={t("account.current")}>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
        </Field>
        <Field label={t("account.new")} hint={t("users.pwHint")}>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
        </Field>
        <Field label={t("account.confirm")}>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </Field>
        {msg && <div className={msg.ok ? "form-ok" : "form-err"}>{msg.text}</div>}
        <div className="brand-actions">
          <Button type="submit" loading={busy}>
            {t("account.update")}
          </Button>
        </div>
      </form>
    </Card>
  );
}
